import * as _ from "lodash";
import * as React from "react";
import { useMemo } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { EditableItemGroup } from "../ItemGroup";

import * as Models from "../../../Models";
import { GENERAL_DETAILS_ATTRIBUTE_GROUP_NAME } from "../../../Constants";

interface ITabbedItemGroupOwnProps {
    dataGroupName: string;
    attributeGroups: Models.AttributeGroup[];
    isCustomerDetail: boolean;
    isInstalledBaseSection: boolean;
    isEditable: boolean;
    editedItemFields: Models.EditableItemField[];
    attributeMap: Models.AttributeMetadata[];
    changeRequests: Models.ChangeRequest[];
    match: Models.Match;

    onChangeFieldValue: (editableField: Models.EditableItemField) => void;
}

interface ITabbedItemGroupProps extends ITabbedItemGroupOwnProps {}

interface ITabbedItemGroupActions {}

interface ITabbedItemGroupState {}

type ComponentProps = ITabbedItemGroupProps & ITabbedItemGroupActions;

export default function TabbedItemGroup(props: ComponentProps) {
    const {
        attributeGroups,
        dataGroupName,
        isEditable,
        editedItemFields,
        attributeMap,
        isCustomerDetail,
        isInstalledBaseSection,
        onChangeFieldValue,
        changeRequests,
        match,
    } = props;

    // Tabs work with attributeGroups, each tab has its own AttributeGroup with rows of grouped attributes
    // Here we first put all the data rows in one list, get the unique tab names, create an AttributeGroup for each of those
    // Then we get the Groups within tab, create group headers and sort alphabetically by group header and then attribute name within group

    const { tabs, panels } = useMemo(() => {
        const rows: Models.AttributeRow[] = [];

        if (attributeGroups.length === 0) {
            return { tabs: [], panels: [] };
        }

        attributeGroups.forEach((group) => {
            group.rows.forEach((row) => {
                if (
                    !row.group &&
                    row.items[0].description
                        .toLowerCase()
                        .startsWith("comments")
                ) {
                    row.group = "Comments";
                }
                rows.push(row);
            });
        });

        // Filter
        const unique = <T,>(value: T, index: number, array: T[]) =>
            array.indexOf(value) === index;

        // Attribute sorter
        const byAttributeName = (
            a: Models.AttributeRow,
            b: Models.AttributeRow
        ) => a.items[0].description.localeCompare(b.items[0].description);

        const tabNames = rows
            .map((r) => r.tab)
            .filter(unique)
            .sort();

        const mappedAttributeGroups: Models.AttributeGroup[] = tabNames.map(
            (tabName) => {
                const tabRows = rows.filter((r) => r.tab === tabName);
                const tabGroups = tabRows
                    .map((r) => r.group)
                    .filter(unique)
                    .sort();

                if (tabGroups.includes("Comments")) {
                    tabGroups.push(
                        tabGroups.splice(tabGroups.indexOf("Comments"), 1)[0] // Comments will be pushed at the bottom of attributes
                    );
                }

                const sortedGroupedTabRows: Models.AttributeRow[] =
                    tabGroups.flatMap((group) => {
                        const groupRows: Models.AttributeRow[] = [];
                        // Group header rows
                        if (group !== "") {
                            groupRows.push({
                                items: [{ description: group }],
                            } as Models.AttributeRow);
                        }

                        return groupRows.concat(
                            tabRows
                                .filter((r) => r.group === group)
                                .sort(byAttributeName)
                        );
                    });

                return {
                    headers: attributeGroups[0].headers,
                    name: tabName,
                    rows: sortedGroupedTabRows,
                    filterableColumns: attributeGroups[0].filterableColumns,
                } as Models.AttributeGroup;
            }
        );

        let tabs = mappedAttributeGroups.map((g) => g.name);
        let panels = mappedAttributeGroups.map((group) => (
            <EditableItemGroup
                key={group.name}
                dataGroupName={dataGroupName}
                isEditable={isEditable}
                editedItemFields={editedItemFields}
                attributeMap={attributeMap}
                isCustomerDetail={isCustomerDetail}
                isInstalledBaseSection={isInstalledBaseSection}
                onChangeFieldValue={onChangeFieldValue}
                attributeGroup={group}
                changeRequests={changeRequests}
                match={match}
                isConfigurationTab={group.name === "Configuration"}
            />
        ));

        // Move General Details tab and panel to first
        const lastTab = _.last(tabs);
        if (lastTab === GENERAL_DETAILS_ATTRIBUTE_GROUP_NAME) {
            tabs.unshift(tabs.pop()!);
            panels.unshift(panels.pop()!);
        }

        return { tabs, panels };
    }, [
        attributeGroups,
        dataGroupName,
        isEditable,
        editedItemFields,
        attributeMap,
        isCustomerDetail,
        isInstalledBaseSection,
        onChangeFieldValue,
        changeRequests,
        match,
    ]);

    if (attributeGroups.length === 0) {
        return (
            <Tabs>
                <TabList />
            </Tabs>
        );
    }

    return (
        <Tabs>
            <TabList>
                {tabs.map((name, index) => (
                    <Tab key={index}>{name}</Tab>
                ))}
            </TabList>
            {panels.map((panel, index) => (
                <TabPanel key={index}>{panel}</TabPanel>
            ))}
        </Tabs>
    );
}
